import isEmpty from '@/helpers/isEmpty'
import {
  FilterFieldOperator,
  SelectionFilterField,
  SortFieldValue,
} from '../../types'

const searchInputKey = 'cbsi'

const sortFieldKey = 'cbs'
const filterFieldPrefix = 'cbf:'

const getFieldValueKey = (prefix: string, field: string) =>
  `${prefix}v-${field}`

const getFieldTextKey = (prefix: string, field: string) => `${prefix}t-${field}`
const getFieldOperatorKey = (prefix: string, field: string) =>
  `${prefix}o-${field}`

const getFieldSearchTextKey = (prefix: string, field: string) =>
  `${prefix}st-${field}`

const getControlBarDataFromUrl = ({
  filterFields,
}: {
  filterFields?: string[]
}) => {
  const filterFieldsMap = new Map<
    string,
    SelectionFilterField<string, string | string[]>
  >()

  const searchParams = new URLSearchParams(window.location.search.slice(1))

  filterFields?.forEach((field) => {
    const values = searchParams
      .getAll(getFieldValueKey(filterFieldPrefix, field))
      .filter((v) => !isEmpty(v))

    const texts = searchParams
      .getAll(getFieldTextKey(filterFieldPrefix, field))
      .filter((v) => !isEmpty(v))

    const searchText =
      searchParams.get(getFieldSearchTextKey(filterFieldPrefix, field)) ||
      undefined

    const operator = (searchParams.get(
      getFieldOperatorKey(filterFieldPrefix, field)
    ) || undefined) as FilterFieldOperator | undefined

    if (isEmpty(texts[0])) {
      return
    }

    if (field.endsWith('[]')) {
      if (values.length === texts.length) {
        values.forEach((value, valueIndex) => {
          const text = texts[valueIndex]

          if (isEmpty(text)) {
            return
          }

          filterFieldsMap.set(`${field}_${valueIndex}`, {
            value,
            text,
            field,
            searchText,
            operator,
          })
        })
      } else {
        filterFieldsMap.set(field, {
          value: values,
          text: texts[0],
          field,
          searchText,
          operator,
        })
      }
    } else {
      filterFieldsMap.set(field, {
        value: values[0],
        text: texts[0],
        field,
        searchText,
        operator,
      })
    }
  })

  return {
    filterFieldsMap,
    searchInputValue: searchParams.get(searchInputKey),
    sortFieldValue: searchParams.get(sortFieldKey),
  }
}

const setControlBarDataInUrl = ({
  filterFields,
  sortValue,
  searchInputValue,
}: {
  filterFields?: SelectionFilterField[]
  sortValue?: SortFieldValue
  searchInputValue?: string
}) => {
  if (
    !Array.isArray(filterFields) &&
    isEmpty(sortValue) &&
    typeof searchInputValue !== 'string'
  ) {
    return
  }

  const url = new URL(window.location.href)

  for (const key of Array.from(url.searchParams.keys())) {
    if (key.startsWith(filterFieldPrefix) && Array.isArray(filterFields)) {
      url.searchParams.delete(key)

      continue
    }

    if (key === sortFieldKey && sortValue === null) {
      url.searchParams.delete(key)

      continue
    }

    if (
      key === searchInputKey &&
      typeof searchInputValue === 'string' &&
      isEmpty(searchInputValue)
    ) {
      url.searchParams.delete(key)

      continue
    }
  }

  filterFields?.forEach(({ field, value, text, searchText, operator }) => {
    if (isEmpty(text)) {
      return
    }

    if (Array.isArray(value)) {
      value.forEach((v) => {
        url.searchParams.append(
          getFieldValueKey(filterFieldPrefix, field),
          v.toString()
        )
      })
    } else if (
      typeof value === 'number' ||
      typeof value === 'string' ||
      typeof value === 'boolean'
    ) {
      if (url.searchParams.has(getFieldValueKey(filterFieldPrefix, field))) {
        url.searchParams.append(
          getFieldValueKey(filterFieldPrefix, field),
          value.toString()
        )
      } else {
        url.searchParams.set(
          getFieldValueKey(filterFieldPrefix, field),
          value.toString()
        )
      }
    }

    if (url.searchParams.has(getFieldTextKey(filterFieldPrefix, field))) {
      url.searchParams.append(getFieldTextKey(filterFieldPrefix, field), text)
    } else {
      url.searchParams.set(getFieldTextKey(filterFieldPrefix, field), text)
    }

    if (!isEmpty(searchText)) {
      url.searchParams.set(
        getFieldSearchTextKey(filterFieldPrefix, field),
        searchText
      )
    }

    if (!isEmpty(operator)) {
      url.searchParams.set(
        getFieldOperatorKey(filterFieldPrefix, field),
        operator
      )
    }
  })

  if (!isEmpty(sortValue)) {
    url.searchParams.set(sortFieldKey, sortValue.toString())
  }

  if (!isEmpty(searchInputValue)) {
    url.searchParams.set(searchInputKey, searchInputValue.trim())
  }

  window.history.replaceState(null, document.title, url.toString())
}

export { getControlBarDataFromUrl, setControlBarDataInUrl }
